import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, Typography, Divider } from '@mui/material';
import debounce from 'debounce';
import Field from '../../../interfaces/field';
import { FieldAliasRow } from '../types/fieldAliasTypes';
import BaseFieldRow from './BaseFieldRow';


interface CustomFieldRowProps {
    row: FieldAliasRow;
    index: number;
    updateRow: (id: string, field: keyof FieldAliasRow, value: any) => void;
    removeRow: (id: string) => void;
    undoDelete: (id: string) => void;
    isDuplicateAliasName: (aliasName: string, currentId: string) => boolean;
    secondaryFields: Field[];
    handleFieldSelection: (rowId: string, selectedFieldId: string) => void;
    handleTextChange?: (field: keyof FieldAliasRow, value: string) => void;
}

const CustomFieldRow: React.FC<CustomFieldRowProps> = (props) => {
    const { row, updateRow, handleTextChange } = props;
    
    // Local state for immediate UI feedback
    const [localFieldLabel, setLocalFieldLabel] = useState(row.field_label || '');
    const [localFieldDescription, setLocalFieldDescription] = useState(row.field_description || '');
    
    // Sync external changes to local state
    useEffect(() => {
        setLocalFieldLabel(row.field_label || '');
        setLocalFieldDescription(row.field_description || '');
    }, [row.field_label, row.field_description]);
    
    // Create debounced update function
    const debouncedUpdateRef = useRef<ReturnType<typeof debounce>>();
    
    const debouncedUpdate = useCallback((field: keyof FieldAliasRow, value: string) => {
        updateRow(row.id, field, value);
    }, [updateRow, row.id]);
    
    useEffect(() => {
        debouncedUpdateRef.current = debounce(debouncedUpdate, 300);
        
        return () => {
            debouncedUpdateRef.current?.clear();
        };
    }, [debouncedUpdate]);
    
    // Handle local text changes
    const handleLocalTextChange = (field: keyof FieldAliasRow, value: string) => {
        if (field === 'field_label') {
            setLocalFieldLabel(value);
        } else if (field === 'field_description') {
            setLocalFieldDescription(value);
        }
        
        debouncedUpdateRef.current?.(field, value);
    };

    return (
        <BaseFieldRow {...props} stateType="full">
            {/* Show field details when a field is selected (read-only) */}
            {row.field_id && !row.customer_detail_field_id && row.is_active && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" mb={2} color="primary">
                        Selected Field Details (Read-only)
                    </Typography>

                    <Box display="flex" gap={2} mb={2}>
                        <Box width="33%">
                            <TextField
                                label="Field Name"
                                value={row.field_name || ''}
                                fullWidth
                                size="small"
                                disabled={true}
                            />
                        </Box>
                        <Box width="33%">
                            <TextField
                                label="Field Type"
                                value={row.field_type || ''}
                                fullWidth
                                size="small"
                                disabled={true}
                            />
                        </Box>
                        <Box width="33%">
                            <TextField
                                label="Value Type"
                                value={row.field_value_type || ''}
                                fullWidth
                                size="small"
                                disabled={true}
                            />
                        </Box>
                    </Box>

                    <Box display="flex" gap={2}>
                        <Box width="50%">
                            <TextField
                                label="Field Label (Editable)"
                                value={localFieldLabel}
                                onChange={(e) => handleLocalTextChange('field_label', e.target.value)}
                                fullWidth
                                size="small"
                                disabled={false}
                            />
                        </Box>
                        <Box width="50%">
                            <TextField
                                label="Field Description (Editable)"
                                value={localFieldDescription}
                                onChange={(e) => handleLocalTextChange('field_description', e.target.value)}
                                fullWidth
                                size="small"
                                disabled={false}
                            />
                        </Box>
                    </Box>
                </>
            )}

            {/* Show message when no field is selected */}
            {!row.field_id && !row.customer_detail_field_id && row.is_active && (
                <Box mt={2} p={2} sx={{ backgroundColor: '#fff3e0', borderRadius: 1 }}>
                    <Typography variant="body2" color="textSecondary" align="center">
                        Please select an existing field from the dropdown above, or click "Create Field" to create a new one.
                    </Typography>
                </Box>
            )}
        </BaseFieldRow>
    );
};

export default CustomFieldRow;